import { TrackChangesParser } from '../word/utils/TrackChangesParser';
import type {
  BankOfChangesMulti,
  BankOfChangesOption,
  BankOfChangesValue,
  CrossCheckClause,
  CrossCheckInsight,
  PastAgreementOption,
} from '../types/crossCheck';
import { formatClauseNumberForDisplay } from '../types/crossCheck';
import { detectLanguage, type SupportedLanguage } from './languageDetection';
import { PLAYBOOK_CONTEXT_MAX_CHARS } from './playbookContext';
import type { PlaybookArchiveRow } from './playbookRecordIndex';
import { buildPlaybookRecordNumberById } from './playbookRecordIndex';

export type AskAgentDomain = 'construction-loan' | 'real-estate';

/** What to include in Ask Agent context when both BoC and past negotiations exist on the card. */
export type AskAgentContextSource = 'bank_of_changes' | 'past_negotiations' | 'both';

export type AskAgentCopy = {
  contextSourceQuestion: string;
  contextSourceChoiceBank: string;
  contextSourceChoiceArchive: string;
  contextSourceChoiceBoth: string;
  contextSourceAppliedBank: string;
  contextSourceAppliedArchive: string;
  contextSourceAppliedBoth: string;
  contextSourceUnrecognized: string;
  initialQuestionArchive: string;
  initialQuestionMcc: string;
  initialQuestionBocOnly: string;
  initialQuestionBocAndArchiveCl: string;
  noMatchesNotice: string;
  noMatchesNoticeMcc: string;
  noMatchesNoPlaybookNotice: string;
  noMatchesNoPlaybookNoticeMcc: string;
  deepResearchQuestion: string;
  deepResearchDeclinedReply: string;
  deepResearchAffirmativeLabel: string;
  suggestEditOffer: string;
  suggestEditDeclinedReply: string;
  suggestEditAppliedReply: string;
  suggestEditApplyLabel: string;
  suggestEditDeclineLabel: string;
  suggestEditApplyUserMessage: string;
  suggestEditApplyErrorPrefix: string;
  composerPlaceholderDefault: string;
  composerPlaceholderContextSource: string;
  composerPlaceholderDeepResearch: string;
  recommendationOpeningPrefix: string;
  recommendationLabelAccept: string;
  recommendationLabelDecline: string;
  recommendationLabelSuggestEdit: string;
  recommendationExplanationPrefix: string;
  feedbackButtonLabel: string;
  feedbackPromptReply: string;
  feedbackUserMessageLabel: string;
  feedbackComposerPlaceholder: string;
  feedbackProcessingErrorPrefix: string;
  feedbackAcceptedVersionNote: string;
};

const ASK_AGENT_COPY: Record<SupportedLanguage, AskAgentCopy> = {
  he: {
    contextSourceQuestion:
      'על מה אתה רוצה שאבסס את התשובה?\n\n' +
      '• בנק השינויים\n' +
      '• ארכיון המשא ומתן של הפרויקט\n' +
      '• שניהם',
    contextSourceChoiceBank: 'בנק השינויים',
    contextSourceChoiceArchive: 'ארכיון המשא ומתן',
    contextSourceChoiceBoth: 'שניהם',
    contextSourceAppliedBank: 'מעולה — אבסס את התשובה על בנק השינויים ובקשת הלקוח.',
    contextSourceAppliedArchive:
      'מעולה — אבסס את התשובה על התאמות משא ומתן קודם ועל ארכיון המשא ומתן של הפרויקט.',
    contextSourceAppliedBoth:
      'מעולה — אבסס את התשובה על בנק השינויים, משא ומתן קודם, וארכיון הפרויקט.',
    contextSourceUnrecognized:
      'לא זיהיתי את הבחירה. בחר/י: בנק השינויים, ארכיון המשא ומתן של הפרויקט, או שניהם — או השתמש/י בכפתורים למטה.',
    initialQuestionArchive:
      'בהתבסס על ארכיון המשא ומתן של הפרויקט, מה ההמלצה שלך — לקבל את בקשת הלקוח כפי שהיא, לדחות, או להציע עריכה? אנא הסבר בקצרה.',
    initialQuestionMcc:
      'בהתבסס על התאמת הסעיף בהסכם המכר, בנק השינויים, סעיפים קיימים רלוונטיים, וארכיון המשא ומתן של הפרויקט — מה ההמלצה שלך לגבי בקשת הלקוח (לקבל, לדחות, או להציע עריכה)? אנא הסבר בקצרה.',
    initialQuestionBocOnly:
      'בהתבסס על בנק השינויים ועל בקשת הלקוח, מה ההמלצה שלך — לקבל את הבקשה כפי שהיא, לדחות, או להציע עריכה? אנא הסבר בקצרה.',
    initialQuestionBocAndArchiveCl:
      'בהתבסס על בנק השינויים, על התאמות משא ומתן קודם לסעיף, ועל ארכיון המשא ומתן של הפרויקט — מה ההמלצה שלך לגבי בקשת הלקוח (לקבל, לדחות, או להציע עריכה)? אנא הסבר בקצרה.',
    noMatchesNotice:
      'לא נמצאו משא ומתן קודם שתואמים ישירות לסעיף זה.\n\n' +
      'האם תרצה שאבצע חיפוש מעמיק בכל הארכיון (תשובות מאת הצד שלנו במשא ומתן בחוזי העבר) כדי לאתר מקורות עקיפים שיכולים לסייע בהחלטה כיצד להגיב לבקשת הלקוח?',
    noMatchesNoticeMcc:
      'לא נמצאו התאמות ישירות לסעיף זה (הסכם מכר / בנק שינויים / סעיפים קיימים).\n\n' +
      'האם תרצה שאבצע חיפוש מעמיק בארכיון המשא ומתן של הפרויקט כדי לאתר מקורות עקיפים שיכולים לסייע בהחלטה כיצד להגיב לבקשת הלקוח?',
    noMatchesNoPlaybookNotice:
      'לא נמצאו משא ומתן קודם לסעיף זה, ואין רשומות בארכיון של הפרויקט. לא ניתן לבצע חיפוש מעמיק.',
    noMatchesNoPlaybookNoticeMcc:
      'לא נמצאו התאמות ישירות לסעיף זה, ואין רשומות בארכיון של הפרויקט. לא ניתן לבצע חיפוש מעמיק.',
    deepResearchQuestion:
      'בצע חיפוש מעמיק בכל הארכיון. על בסיס מקורות עקיפים בלבד (ללא התאמה ישירה קודמת לסעיף), ' +
      'מה ההמלצה — לקבל את בקשת הלקוח כפי שהיא, לדחות, או להציע עריכה? הסבר בקצרה.',
    deepResearchDeclinedReply: 'בסדר. ניתן לשאול שאלה אחרת על הארכיון או על בקשת הלקוח.',
    deepResearchAffirmativeLabel: 'כן, חפש בארכיון',
    suggestEditOffer:
      'ההמלצה היא להציע עריכה. האם תרצה שאכין נוסח מוצע **צר** לבקשת הלקוח — רק לפי מה שהלקוח מבקש, ולא שינויים רחבים יותר מהמקורות?',
    suggestEditDeclinedReply: 'בסדר. ניתן להמשיך לשאול שאלות או להפעיל + AI על הכרטיס ידנית.',
    suggestEditAppliedReply: 'העריכה המוצעת הוחלה על בקשת הלקוח בכרטיס הסעיף. ניתן לעיין ולשלוח לתגובה.',
    suggestEditApplyLabel: 'כן, הצע עריכה',
    suggestEditDeclineLabel: 'לא, תודה',
    suggestEditApplyUserMessage: 'כן, הצע עריכה',
    suggestEditApplyErrorPrefix: 'לא הצלחתי להחיל את העריכה:',
    composerPlaceholderDefault: 'שאל על משא ומתן קודם…',
    composerPlaceholderContextSource: 'בחר/י מקור או הקלד: בנק השינויים / ארכיון / שניהם…',
    composerPlaceholderDeepResearch: 'הקלד כן או לא, או שאל שאלה…',
    recommendationOpeningPrefix: 'ההמלצה',
    recommendationLabelAccept: 'לקבל את בקשת הלקוח כפי שהיא',
    recommendationLabelDecline: 'לדחות את בקשת הלקוח',
    recommendationLabelSuggestEdit: 'להציע עריכה',
    recommendationExplanationPrefix: 'הסבר',
    feedbackButtonLabel: 'פידבק',
    feedbackPromptReply:
      'אשמח לקבל פידבק על ההמלצה שמסרתי, כדי שאוכל לשפר את ההמלצות בפעם הבאה. מה היית משנה או מוסיף?',
    feedbackUserMessageLabel: 'פידבק',
    feedbackComposerPlaceholder: 'שתף/י את הפידבק שלך על ההמלצה…',
    feedbackProcessingErrorPrefix: 'לא הצלחתי לעבד את הפידבק:',
    feedbackAcceptedVersionNote: 'גרסה',
  },
  en: {
    contextSourceQuestion:
      'What should I base my answer on?\n\n' +
      '• Bank of Changes\n' +
      '• Project negotiation archive\n' +
      '• Both',
    contextSourceChoiceBank: 'Bank of Changes',
    contextSourceChoiceArchive: 'Negotiation archive',
    contextSourceChoiceBoth: 'Both',
    contextSourceAppliedBank:
      'Got it — I will base my answer on the Bank of Changes and the client request.',
    contextSourceAppliedArchive:
      'Got it — I will base my answer on matched past negotiations and the project negotiation archive.',
    contextSourceAppliedBoth:
      'Got it — I will use the Bank of Changes, past negotiation matches, and the project archive.',
    contextSourceUnrecognized:
      'I did not recognize that choice. Pick Bank of Changes, project negotiation archive, or Both — or use the buttons below.',
    initialQuestionArchive:
      "Based on the project's negotiation archive, what is your recommendation — accept the client's request as is, decline it, or suggest an edit? Please explain briefly.",
    initialQuestionMcc:
      "Based on the matched sales-agreement clause, Bank of Changes, relevant existing clauses, and the project's negotiation archive — what is your recommendation on the client request (accept, decline, or suggest an edit)? Please explain briefly.",
    initialQuestionBocOnly:
      "Based on the Bank of Changes and the client request, what is your recommendation — accept the request as is, decline it, or suggest an edit? Please explain briefly.",
    initialQuestionBocAndArchiveCl:
      "Based on the Bank of Changes, past negotiation matches for this clause, and the project's negotiation archive — what is your recommendation on the client request (accept, decline, or suggest an edit)? Please explain briefly.",
    noMatchesNotice:
      'No past negotiations directly match this clause.\n\n' +
      'Would you like me to search the full archive (our-side responses from prior deals) for indirect sources that may help decide how to respond to the client request?',
    noMatchesNoticeMcc:
      'No direct matches for this clause (sales agreement / Bank of Changes / existing clauses).\n\n' +
      'Would you like me to search the project negotiation archive for indirect sources that may help decide how to respond to the client request?',
    noMatchesNoPlaybookNotice:
      'No past negotiations match this clause, and the project archive has no records. A deep archive search is not available.',
    noMatchesNoPlaybookNoticeMcc:
      'No direct matches for this clause, and the project archive has no records. A deep archive search is not available.',
    deepResearchQuestion:
      'Search the full archive deeply. Based only on indirect sources (no prior direct match for this clause), what is your recommendation — accept the client request as is, decline it, or suggest an edit? Explain briefly.',
    deepResearchDeclinedReply:
      'OK. You can ask another question about the archive or the client request.',
    deepResearchAffirmativeLabel: 'Yes, search the archive',
    suggestEditOffer:
      'The recommendation is to suggest an edit. Should I draft **narrow** proposed wording for the client request — only what the client is asking for, not broader changes than the sources support?',
    suggestEditDeclinedReply:
      'OK. You can keep asking questions or use + AI on the clause card manually.',
    suggestEditAppliedReply:
      'The suggested edit was applied to the client request on the clause card. Review and send your response.',
    suggestEditApplyLabel: 'Yes, suggest edit',
    suggestEditDeclineLabel: 'No, thanks',
    suggestEditApplyUserMessage: 'Yes, suggest edit',
    suggestEditApplyErrorPrefix: 'Could not apply the suggested edit:',
    composerPlaceholderDefault: 'Ask about past negotiations…',
    composerPlaceholderContextSource:
      'Choose a source or type: Bank of Changes / archive / both…',
    composerPlaceholderDeepResearch: 'Type yes or no, or ask a question…',
    recommendationOpeningPrefix: 'The recommendation',
    recommendationLabelAccept: "accept the client's request as is",
    recommendationLabelDecline: "decline the client's request",
    recommendationLabelSuggestEdit: 'suggest an edit',
    recommendationExplanationPrefix: 'Explanation',
    feedbackButtonLabel: 'Feedback',
    feedbackPromptReply:
      "I'd love your feedback on the recommendation I gave so I can improve next time. What would you change or add?",
    feedbackUserMessageLabel: 'Feedback',
    feedbackComposerPlaceholder: 'Share your feedback on the recommendation…',
    feedbackProcessingErrorPrefix: 'Could not process your feedback:',
    feedbackAcceptedVersionNote: 'Version',
  },
};

export function getAskAgentCopy(language: SupportedLanguage = 'he'): AskAgentCopy {
  return ASK_AGENT_COPY[language];
}

/** @deprecated Use getAskAgentCopy(language).contextSourceQuestion */
export const ASK_AGENT_CONTEXT_SOURCE_QUESTION = ASK_AGENT_COPY.he.contextSourceQuestion;
/** @deprecated Use getAskAgentCopy(language) */
export const ASK_AGENT_CONTEXT_SOURCE_CHOICE_BANK = ASK_AGENT_COPY.he.contextSourceChoiceBank;
/** @deprecated Use getAskAgentCopy(language) */
export const ASK_AGENT_CONTEXT_SOURCE_CHOICE_ARCHIVE = ASK_AGENT_COPY.he.contextSourceChoiceArchive;
/** @deprecated Use getAskAgentCopy(language) */
export const ASK_AGENT_CONTEXT_SOURCE_CHOICE_BOTH = ASK_AGENT_COPY.he.contextSourceChoiceBoth;
/** @deprecated Use getAskAgentCopy(language) */
export const ASK_AGENT_CONTEXT_SOURCE_APPLIED_BANK = ASK_AGENT_COPY.he.contextSourceAppliedBank;
/** @deprecated Use getAskAgentCopy(language) */
export const ASK_AGENT_CONTEXT_SOURCE_APPLIED_ARCHIVE = ASK_AGENT_COPY.he.contextSourceAppliedArchive;
/** @deprecated Use getAskAgentCopy(language) */
export const ASK_AGENT_CONTEXT_SOURCE_APPLIED_BOTH = ASK_AGENT_COPY.he.contextSourceAppliedBoth;
/** @deprecated Use getAskAgentCopy(language) */
export const ASK_AGENT_CONTEXT_SOURCE_UNRECOGNIZED = ASK_AGENT_COPY.he.contextSourceUnrecognized;
/** @deprecated Use getAskAgentInitialQuestion(domain, language) */
export const ASK_AGENT_INITIAL_QUESTION = ASK_AGENT_COPY.he.initialQuestionArchive;
/** @deprecated Use getAskAgentInitialQuestion(domain, language) */
export const ASK_AGENT_INITIAL_QUESTION_MCC = ASK_AGENT_COPY.he.initialQuestionMcc;
/** @deprecated Use getAskAgentNoMatchesNotice(domain, language) */
export const ASK_AGENT_NO_MATCHES_NOTICE = ASK_AGENT_COPY.he.noMatchesNotice;
/** @deprecated Use getAskAgentNoMatchesNotice(domain, language) */
export const ASK_AGENT_NO_MATCHES_NOTICE_MCC = ASK_AGENT_COPY.he.noMatchesNoticeMcc;
/** @deprecated Use getAskAgentNoMatchesNoPlaybookNotice(domain, language) */
export const ASK_AGENT_NO_MATCHES_NO_PLAYBOOK_NOTICE = ASK_AGENT_COPY.he.noMatchesNoPlaybookNotice;
/** @deprecated Use getAskAgentNoMatchesNoPlaybookNotice(domain, language) */
export const ASK_AGENT_NO_MATCHES_NO_PLAYBOOK_NOTICE_MCC =
  ASK_AGENT_COPY.he.noMatchesNoPlaybookNoticeMcc;
/** @deprecated Use getAskAgentCopy(language) */
export const ASK_AGENT_DEEP_RESEARCH_QUESTION = ASK_AGENT_COPY.he.deepResearchQuestion;
/** @deprecated Use getAskAgentCopy(language) */
export const ASK_AGENT_DEEP_RESEARCH_DECLINED_REPLY = ASK_AGENT_COPY.he.deepResearchDeclinedReply;

export function getAskAgentInitialQuestion(
  domain: AskAgentDomain,
  language: SupportedLanguage = 'he',
): string {
  const copy = getAskAgentCopy(language);
  return domain === 'construction-loan' ? copy.initialQuestionArchive : copy.initialQuestionMcc;
}

export function getAskAgentNoMatchesNotice(
  domain: AskAgentDomain,
  language: SupportedLanguage = 'he',
): string {
  const copy = getAskAgentCopy(language);
  return domain === 'construction-loan' ? copy.noMatchesNotice : copy.noMatchesNoticeMcc;
}

export function getAskAgentNoMatchesNoPlaybookNotice(
  domain: AskAgentDomain,
  language: SupportedLanguage = 'he',
): string {
  const copy = getAskAgentCopy(language);
  return domain === 'construction-loan'
    ? copy.noMatchesNoPlaybookNotice
    : copy.noMatchesNoPlaybookNoticeMcc;
}

export function clauseHasMatchedPastNegotiations(clause: CrossCheckClause): boolean {
  const options = clause.past_agreement?.options ?? [];
  return options.length > 0;
}

export function clauseHasBankOfChangesForAsk(clause: CrossCheckClause): boolean {
  return getBankOfChangesOptionsForClause(clause).length > 0;
}

/** True when the user must pick BoC vs project archive vs both before the first agent question. */
export function clauseNeedsAskAgentContextSourceChoice(clause: CrossCheckClause): boolean {
  return clauseHasBankOfChangesForAsk(clause) && clauseHasMatchedPastNegotiations(clause);
}

export function parseAskAgentContextSourceChoice(text: string): AskAgentContextSource | null {
  const t = text.trim().toLowerCase();
  if (!t) return null;
  if (/שניהם|שתיהן|both|גם וגם|הכל|כולם/.test(t)) return 'both';
  if (/^3$/.test(t)) return 'both';
  if (/בנק\s*השינויים|בנק\s*שינויים|^בנק$|שינויים|boc|bank\s*of\s*changes/.test(t)) {
    return 'bank_of_changes';
  }
  if (/^1$/.test(t)) return 'bank_of_changes';
  if (
    /ארכיון|משא\s*ומתן|משא ומתן|ארכיון\s*הפרויקט|archive|past\s*negotiation/.test(t)
  ) {
    return 'past_negotiations';
  }
  if (/^2$/.test(t)) return 'past_negotiations';
  return null;
}

export function getAskAgentInitialQuestionForSource(
  source: AskAgentContextSource,
  domain: AskAgentDomain,
  language: SupportedLanguage = 'he',
): string {
  const copy = getAskAgentCopy(language);
  if (source === 'bank_of_changes') {
    return copy.initialQuestionBocOnly;
  }
  if (source === 'past_negotiations') {
    return getAskAgentInitialQuestion(domain, language);
  }
  if (domain === 'construction-loan') {
    return copy.initialQuestionBocAndArchiveCl;
  }
  return copy.initialQuestionMcc;
}

export function getAskAgentContextSourceAppliedReply(
  source: AskAgentContextSource,
  language: SupportedLanguage = 'he',
): string {
  const copy = getAskAgentCopy(language);
  if (source === 'bank_of_changes') return copy.contextSourceAppliedBank;
  if (source === 'past_negotiations') return copy.contextSourceAppliedArchive;
  return copy.contextSourceAppliedBoth;
}

/** Same resolution as CrossCheckClauseCard `getBocOptions` — all card BoC rows. */
export function getBocOptionsFromClause(clause: CrossCheckClause): BankOfChangesOption[] {
  const boc = clause.bank_of_changes;
  if (!boc) return [];
  if (typeof boc === 'string') {
    const t = boc.trim();
    return t
      ? [{ text: t, score: 0, rank: 1, original_extracted: t }]
      : [];
  }
  if (typeof boc === 'object' && boc !== null && 'options' in boc && Array.isArray(boc.options)) {
    return boc.options as BankOfChangesOption[];
  }
  if (Array.isArray(boc)) {
    return boc as BankOfChangesOption[];
  }
  if (typeof boc === 'object' && boc !== null && 'chosen_option' in boc) {
    const co = String((boc as BankOfChangesMulti).chosen_option || '').trim();
    return co
      ? [{ text: co, score: 0, rank: 1, original_extracted: co }]
      : [];
  }
  return [];
}

export function resolveBocOptionDisplayText(opt: BankOfChangesOption): string {
  return (opt.text?.trim() || opt.original_extracted?.trim() || '').trim();
}

/** Normalized bank-of-changes options for Ask Agent context (matches card list). */
export function getBankOfChangesOptions(
  boc: BankOfChangesValue | undefined,
): Array<{
  text: string;
  score?: number;
  rank?: number;
  original_extracted?: string;
  internal_note?: string;
}> {
  if (!boc) return [];
  if (typeof boc === 'string') {
    const t = boc.trim();
    return t ? [{ text: t, rank: 1, original_extracted: t }] : [];
  }
  if ('options' in boc && Array.isArray(boc.options)) {
    return (boc.options as BankOfChangesOption[])
      .map((o, i) => ({
        text: resolveBocOptionDisplayText(o),
        score: o.score,
        rank: o.rank ?? i + 1,
        original_extracted: o.original_extracted,
        internal_note: o.internal_note,
      }))
      .filter((o) => o.text);
  }
  if (Array.isArray(boc)) {
    return (boc as BankOfChangesOption[])
      .map((o, i) => ({
        text: resolveBocOptionDisplayText(o),
        score: o.score,
        rank: o.rank ?? i + 1,
        original_extracted: o.original_extracted,
        internal_note: o.internal_note,
      }))
      .filter((o) => o.text);
  }
  if ('chosen_option' in boc && boc.chosen_option) {
    const t = String(boc.chosen_option).trim();
    return t ? [{ text: t, rank: 1, original_extracted: t }] : [];
  }
  return [];
}

export function getBankOfChangesOptionsForClause(
  clause: CrossCheckClause,
): ReturnType<typeof getBankOfChangesOptions> {
  return getBocOptionsFromClause(clause)
    .map((o, i) => ({
      text: resolveBocOptionDisplayText(o),
      score: o.score,
      rank: o.rank ?? i + 1,
      original_extracted: o.original_extracted,
      internal_note: o.internal_note,
    }))
    .filter((o) => o.text);
}

/** Whether the clause already has cross-check matches to ground the first agent question. */
export function clauseHasAskAgentGrounding(
  clause: CrossCheckClause,
  domain: AskAgentDomain,
): boolean {
  if (domain === 'construction-loan') {
    return clauseHasMatchedPastNegotiations(clause);
  }
  if ((clause.sales_agreement || '').trim()) return true;
  if (getBankOfChangesOptionsForClause(clause).length > 0) return true;
  if ((clause.insights?.length ?? 0) > 0) return true;
  return false;
}

const DEEP_RESEARCH_AFFIRMATIVE =
  /^(כן|כן[,\s]|yes|y|ok|אוקי|אשמח|בצע|חפש|חפשי|בבקשה|please|go ahead)/i;
const DEEP_RESEARCH_NEGATIVE = /^(לא|לא\.|no|n|לא תודה|תודה)/i;

export function isDeepResearchAffirmative(text: string): boolean {
  const t = text.trim();
  return DEEP_RESEARCH_AFFIRMATIVE.test(t);
}

export function isDeepResearchDecline(text: string): boolean {
  const t = text.trim();
  return DEEP_RESEARCH_NEGATIVE.test(t);
}

/** Machine-parseable recommendation enum returned by the backend playbook-ask endpoint. */
export type AskAgentRecommendation =
  | 'accept_as_is'
  | 'decline'
  | 'suggest_edit'
  | 'unknown';

function recommendationLabelFor(
  recommendation: Exclude<AskAgentRecommendation, 'unknown'>,
  copy: AskAgentCopy,
): string {
  switch (recommendation) {
    case 'accept_as_is':
      return copy.recommendationLabelAccept;
    case 'decline':
      return copy.recommendationLabelDecline;
    case 'suggest_edit':
      return copy.recommendationLabelSuggestEdit;
  }
}

/** Remove leading recommendation / explanation labels the model may have already written. */
function stripDuplicateRecommendationOpening(text: string): string {
  let t = text.trim();
  const patterns = [
    /^(?:\*\*)?הה?מלצה(?:\*\*)?\s*:\s*.+?(?:\.\s*)?(?:\n\n|\n|$)/u,
    /^(?:\*\*)?(?:The )?[Rr]ecommendation(?:\*\*)?\s*:\s*.+?(?:\.\s*)?(?:\n\n|\n|$)/u,
    /^(?:\*\*)?הסבר(?:\s+קצר)?(?:\*\*)?\s*:\s*/u,
    /^(?:\*\*)?[Ee]xplanation(?:\*\*)?\s*:\s*/u,
  ];
  for (const pattern of patterns) {
    const next = t.replace(pattern, '').trim();
    if (next !== t) {
      t = next;
    }
  }
  return t;
}

/**
 * Prefix the model explanation with a deterministic recommendation opening derived
 * from the structured backend enum (not from free-text parsing).
 */
export function formatAskAgentRecommendationAnswer(
  recommendation: AskAgentRecommendation,
  explanation: string,
  language: SupportedLanguage = 'he',
): string {
  const trimmed = explanation.trim();
  if (!trimmed || recommendation === 'unknown') {
    return trimmed;
  }

  const copy = getAskAgentCopy(language);
  const label = recommendationLabelFor(recommendation, copy);
  const body = stripDuplicateRecommendationOpening(trimmed);
  const opening = `**${copy.recommendationOpeningPrefix}:** ${label}.`;
  if (!body) {
    return opening;
  }
  const explanationBlock = `**${copy.recommendationExplanationPrefix}:** ${body}`;
  return `${opening}\n\n${explanationBlock}`;
}

/**
 * @deprecated The backend now returns a structured `recommendation` field on the
 * playbook-ask response. Use that enum directly instead of parsing the assistant
 * answer text. Kept only as a fallback for older responses.
 */
export function isSuggestEditRecommendation(text: string): boolean {
  const t = text.trim().replace(/\*\*/g, '');
  return (
    /המלצה\s*:\s*ה?צע\s*עריכה/i.test(t) ||
    /המלצה\s*:\s*להציע\s*עריכה/i.test(t) ||
    /להציע\s*עריכה/i.test(t) ||
    /הצע\s*עריכה/i.test(t) ||
    /הציע\s*עריכה/i.test(t) ||
    /recommendation\s*:\s*suggest\s*edit/i.test(t) ||
    /suggest\s*edit/i.test(t)
  );
}

/** @deprecated Use getAskAgentCopy(language) */
export const ASK_AGENT_SUGGEST_EDIT_OFFER = ASK_AGENT_COPY.he.suggestEditOffer;
/** @deprecated Use getAskAgentCopy(language) */
export const ASK_AGENT_SUGGEST_EDIT_DECLINED_REPLY = ASK_AGENT_COPY.he.suggestEditDeclinedReply;
/** @deprecated Use getAskAgentCopy(language) */
export const ASK_AGENT_SUGGEST_EDIT_APPLIED_REPLY = ASK_AGENT_COPY.he.suggestEditAppliedReply;

export type SuggestEditSourceType = 'bank_of_changes' | 'past_agreement' | 'both';

/** Maps Ask Agent context choice to engine `source_type` for suggestion stream. */
export function suggestEditSourceTypeFromContext(
  contextSource: AskAgentContextSource | null | undefined,
): SuggestEditSourceType {
  if (contextSource === 'bank_of_changes') return 'bank_of_changes';
  if (contextSource === 'past_negotiations') return 'past_agreement';
  return 'both';
}

/**
 * Reference for suggest-edit: clause sources (BoC / past matches) + agent rationale,
 * scoped so the model edits only what the client requested — not every change in the sources.
 */
export function buildSuggestEditReferenceForClause(
  clause: CrossCheckClause,
  messages: Array<{ role: string; content: string }>,
  contextSource: AskAgentContextSource | null | undefined,
  playbookPlainTextById: Record<string, string>,
  playbookFileNameById: Record<string, string> = {},
  language: SupportedLanguage = 'he',
): { referenceText: string; referenceExplanation: string; sourceType: SuggestEditSourceType } {
  const lastAssistant =
    [...messages].reverse().find((m) => m.role === 'assistant')?.content?.trim() ?? '';
  const interpretation = getInterpretationForAsk(clause);
  const clientPlain = getClientRequestPlainTextForAsk(clause);

  const includeBoc =
    !contextSource || contextSource === 'bank_of_changes' || contextSource === 'both';
  const includePast =
    !contextSource || contextSource === 'past_negotiations' || contextSource === 'both';

  const parts: string[] = [
    '=== SCOPE: MINIMAL EDIT TO CLIENT REQUEST ONLY ===',
    'The reference blocks below may contain full suggested responses or long past-negotiation texts.',
    'Use ONLY the fragments that directly address what the client is asking for in this paragraph.',
    'Ignore any wider, additional, or unrelated changes described in those sources.',
    '',
    '--- What the client is asking for (client request — accepted text) ---',
    clientPlain || '(none)',
    '',
    '--- Interpretation of track changes (what changed / what the client wants) ---',
    interpretation || '(none)',
  ];

  if (includeBoc) {
    const bocOpts = getBankOfChangesOptionsForClause(clause);
    if (bocOpts.length) {
      parts.push(
        '',
        '--- Bank of Changes options (use only phrases relevant to the client ask above) ---',
      );
      bocOpts.forEach((opt, i) => {
        parts.push(...formatBankOfChangesBlock(opt, i + 1));
      });
    } else {
      parts.push('', '--- Bank of Changes: (none on this clause) ---');
    }
  }

  if (includePast) {
    const pastOpts = clause.past_agreement?.options ?? [];
    if (pastOpts.length) {
      parts.push(
        '',
        '--- Matched past negotiations (use only phrases relevant to the client ask above) ---',
      );
      pastOpts.slice(0, 5).forEach((opt, i) => {
        parts.push(
          ...formatPastNegotiationBlock(opt, i + 1, playbookPlainTextById, playbookFileNameById),
        );
      });
    } else {
      parts.push('', '--- Matched past negotiations: (none on this clause) ---');
    }
  }

  const sourceType = suggestEditSourceTypeFromContext(contextSource);

  const explanationParts =
    language === 'en'
      ? [
          'Narrow suggested edit to the client-request paragraph only.',
          'Apply changes only to address what the client is asking for — not everything in the sources.',
          '',
          'Agent recommendation (rationale):',
          lastAssistant || '(none)',
        ]
      : [
          'עריכה מוצעת צרה לפסקת בקשת הלקוח בלבד.',
          'החל שינוי רק כדי לענות על מה שהלקוח מבקש — לא את כל מה שמופיע במקורות.',
          '',
          'המלצת הסוכן (rationale):',
          lastAssistant || '(אין)',
        ];

  return {
    referenceText: parts.join('\n').slice(0, 30_000),
    referenceExplanation: explanationParts.join('\n'),
    sourceType,
  };
}

/** @deprecated Prefer buildSuggestEditReferenceForClause for Ask Agent suggest-edit. */
export function buildChatReferenceForSuggestion(
  messages: Array<{ role: string; content: string }>,
): { referenceText: string; referenceExplanation: string } {
  const lines = messages.map((m) => {
    const label = m.role === 'user' ? 'משתמש' : 'סוכן';
    return `${label}: ${m.content}`;
  });
  const referenceText = lines.join('\n\n').slice(0, 30_000);
  const lastAssistant = [...messages].reverse().find((m) => m.role === 'assistant')?.content ?? '';
  return {
    referenceText,
    referenceExplanation: `המלצת הסוכן והקשר השיחה:\n${lastAssistant}`.trim(),
  };
}

/** Matches Record / רשומה citations in Ask Agent answers (English & Hebrew). */
const PLAYBOOK_RECORD_REF_RE =
  /(?:\[\[\s*(?:Records?|רשומ(?:ה|ות))\s+([\d, ]+?)\s*\]\]|(?:Records?|רשומ(?:ה|ות))\s+(\d+(?:\s*[,،]\s*\d+)*))/gu;

export function extractPlaybookRecordNumbersFromText(text: string): number[] {
  const seen = new Set<number>();
  const re = new RegExp(PLAYBOOK_RECORD_REF_RE.source, PLAYBOOK_RECORD_REF_RE.flags);
  let match: RegExpExecArray | null;
  while ((match = re.exec(text)) !== null) {
    const numberBody = match[1] ?? match[2] ?? '';
    for (const part of numberBody.split(/[,،\s]+/)) {
      const n = parseInt(part, 10);
      if (Number.isFinite(n) && n > 0) {
        seen.add(n);
      }
    }
  }
  return [...seen].sort((a, b) => a - b);
}

export function extractClientRequestFromAskContext(contextBody: string): string {
  const body = (contextBody || '').trim();
  const startMarker = '--- Client request ---';
  const idx = body.indexOf(startMarker);
  if (idx < 0) {
    return '';
  }
  let rest = body.slice(idx + startMarker.length).trim();
  const nextSection = rest.search(/\n--- /);
  if (nextSection >= 0) {
    rest = rest.slice(0, nextSection).trim();
  }
  return rest === '(none)' ? '' : rest;
}

/** Clause-focused prefix (client request, matches, BoC) without the full project playbook archive. */
export function extractClausePrefixFromAskContext(
  contextBody: string,
  maxChars = 2000,
): string {
  const sep = '=== PROJECT PLAYBOOK (past negotiations archive) ===';
  let prefix = (contextBody || '').trim();
  const sepIdx = prefix.indexOf(sep);
  if (sepIdx >= 0) {
    prefix = prefix.slice(0, sepIdx).trim();
  }
  if (prefix.length > maxChars) {
    return `${prefix.slice(0, maxChars)}\n…`;
  }
  return prefix;
}

export function buildPlaybookReferencesSummaryForSkillRevision(
  askAgentAnswer: string,
  rows: PlaybookArchiveRow[],
  maxChars = 4000,
): string {
  const nums = extractPlaybookRecordNumbersFromText(askAgentAnswer);
  if (!nums.length) {
    return '(no playbook archive records cited in the Ask Agent answer)';
  }

  const lines: string[] = ['=== Playbook archive records cited in Ask Agent answer ==='];
  for (const n of nums) {
    const row = rows[n - 1];
    if (!row) {
      lines.push(`Record ${n}: (not found in archive index)`);
      continue;
    }
    const excerpt = (row.paragraph_preview || row.paragraph_text || '').trim().slice(0, 500);
    lines.push(
      `Record ${n}:`,
      `  source_file: ${row.filename || '(unknown)'}`,
      `  author/side: ${row.side || row.author || '(unknown)'}`,
      `  excerpt: ${excerpt || '(empty)'}`,
      '',
    );
  }
  const out = lines.join('\n').trim();
  if (out.length > maxChars) {
    return `${out.slice(0, maxChars)}\n…`;
  }
  return out;
}

export function getClientRequestXml(clause: CrossCheckClause): string | null {
  const cr = clause.client_request?.trim() || '';
  if (
    cr &&
    (cr.startsWith('<w:') ||
      cr.startsWith('<pkg:') ||
      cr.startsWith('<?xml') ||
      cr.includes('<w:p') ||
      cr.includes('<p '))
  ) {
    return cr;
  }
  if (clause.track_changes?.paragraph_xml) {
    return clause.track_changes.paragraph_xml;
  }
  return null;
}

export function getClientRequestPlainTextForAsk(clause: CrossCheckClause): string {
  const xml = getClientRequestXml(clause);
  if (xml) {
    try {
      return TrackChangesParser.extractAcceptedText(xml).replace(/\s+/g, ' ').trim();
    } catch {
      /* fallthrough */
    }
  }
  if (clause.module_type === 'module_b' && clause.client_request) {
    try {
      const parsed = JSON.parse(clause.client_request);
      return (parsed.edited_text || parsed.original_text || clause.client_request).trim();
    } catch {
      return clause.client_request.trim();
    }
  }
  return (clause.client_request || '').trim();
}

export function getInterpretationForAsk(clause: CrossCheckClause): string {
  const fromClause = clause.track_changes_interpretation?.trim();
  if (fromClause) return fromClause;
  const fromInsight = clause.insights?.[0]?.explanation?.trim();
  return fromInsight || '';
}

/** Language for Ask Agent UI and auto-sent prompts — from the open clause text, not the archive. */
export function detectAskAgentLanguage(clause: CrossCheckClause): SupportedLanguage {
  const sample = [
    getClientRequestPlainTextForAsk(clause),
    getInterpretationForAsk(clause),
    (clause.sales_agreement || '').trim(),
  ]
    .filter(Boolean)
    .join('\n')
    .slice(0, 8000);
  return detectLanguage(sample);
}

export function buildClauseAskAgentPrefix(
  clause: CrossCheckClause,
  playbookPlainTextById: Record<string, string>,
  playbookFileNameById: Record<string, string> = {},
  domain: AskAgentDomain = 'construction-loan',
  contextSource?: AskAgentContextSource | null,
  playbookRows?: PlaybookArchiveRow[],
): string {
  const recordNumberById = playbookRows?.length
    ? buildPlaybookRecordNumberById(playbookRows)
    : undefined;
  const includeBoc =
    contextSource == null ||
    contextSource === 'bank_of_changes' ||
    contextSource === 'both';
  const includePast =
    contextSource == null ||
    contextSource === 'past_negotiations' ||
    contextSource === 'both';
  /** While awaiting user source choice, omit BoC and past match blocks from context. */
  const pendingSourceChoice = contextSource == null;
  const effectiveIncludeBoc = pendingSourceChoice ? false : includeBoc;
  const effectiveIncludePast = pendingSourceChoice ? false : includePast;
  const includeSalesAgreement =
    domain === 'real-estate' && contextSource !== 'bank_of_changes';
  const includeInsights =
    domain === 'real-estate' &&
    contextSource !== 'bank_of_changes' &&
    contextSource !== 'past_negotiations';
  const clauseLabel =
    clause.clause_number != null
      ? formatClauseNumberForDisplay(clause.clause_number)
      : '—';
  const clientRequest = getClientRequestPlainTextForAsk(clause);
  const interpretation = getInterpretationForAsk(clause);

  const lines: string[] = [
    '=== CURRENT CLAUSE (open in Word) ===',
    `Clause: ${clauseLabel}`,
    '',
    '--- Client request ---',
    clientRequest || '(none)',
    '',
    '--- Interpretation (track changes) ---',
    interpretation || '(none)',
  ];

  if (domain === 'real-estate') {
    if (includeSalesAgreement) {
      lines.push('', '--- Matched Sales Agreement clause ---');
      const sa = (clause.sales_agreement || '').trim();
      lines.push(sa || '(no sales agreement match for this clause)');
    }

    if (effectiveIncludeBoc) {
      lines.push('', '--- Bank of Changes suggestions for this clause ---');
      const bocOpts = getBankOfChangesOptionsForClause(clause);
      if (!bocOpts.length) {
        lines.push('(no bank of changes suggestions for this clause)');
      } else {
        bocOpts.forEach((opt, i) => {
          lines.push(...formatBankOfChangesBlock(opt, i + 1));
        });
      }
    }

    if (includeInsights) {
      lines.push('', '--- Existing clauses that may already satisfy the request ---');
      const insights = clause.insights ?? [];
      if (!insights.length) {
        lines.push('(no existing-clause insights for this clause)');
      } else {
        insights.slice(0, 5).forEach((ins, i) => {
          lines.push(...formatInsightBlock(ins, i + 1));
        });
      }
    }

    if (effectiveIncludePast) {
      lines.push('', '--- Matched past negotiations for this clause ---');
      const pastOpts = clause.past_agreement?.options ?? [];
      if (!pastOpts.length) {
        lines.push('(no past negotiation matches for this clause)');
      } else {
        pastOpts.slice(0, 5).forEach((opt, i) => {
          lines.push(
            ...formatPastNegotiationBlock(
              opt,
              i + 1,
              playbookPlainTextById,
              playbookFileNameById,
              recordNumberById,
            ),
          );
        });
      }
    }
  } else {
    if (effectiveIncludePast) {
      lines.push('', '--- Matched past negotiations for this clause ---');
      const options = clause.past_agreement?.options ?? [];
      if (!options.length) {
        lines.push('(no past negotiation matches for this clause)');
      } else {
        options.slice(0, 5).forEach((opt, i) => {
          lines.push(
            ...formatPastNegotiationBlock(
              opt,
              i + 1,
              playbookPlainTextById,
              playbookFileNameById,
              recordNumberById,
            ),
          );
        });
      }
    }
    if (effectiveIncludeBoc) {
      lines.push('', '--- Bank of Changes suggestions for this clause ---');
      const bocOpts = getBankOfChangesOptionsForClause(clause);
      if (!bocOpts.length) {
        lines.push('(no bank of changes suggestions for this clause)');
      } else {
        bocOpts.forEach((opt, i) => {
          lines.push(...formatBankOfChangesBlock(opt, i + 1));
        });
      }
    }
  }

  return lines.join('\n');
}

function formatBankOfChangesBlock(
  opt: {
    text: string;
    score?: number;
    rank?: number;
    original_extracted?: string;
    internal_note?: string;
  },
  rank: number,
): string[] {
  const score =
    opt.score != null && !Number.isNaN(Number(opt.score))
      ? `Relevance score: ${opt.score}\n`
      : '';
  const note = opt.internal_note?.trim()
    ? `Internal note: ${opt.internal_note.trim()}\n`
    : null;
  const extracted =
    opt.original_extracted?.trim() &&
    opt.original_extracted.trim() !== opt.text.trim()
      ? `Original extracted text: ${opt.original_extracted.trim()}\n`
      : null;
  return [
    `--- Bank of Changes option ${rank} ---`,
    score ? score.trimEnd() : null,
    note,
    extracted,
    'Suggested response text:',
    opt.text.trim() || '(text unavailable)',
    '',
  ].filter((l) => l !== null) as string[];
}

function formatInsightBlock(ins: CrossCheckInsight, rank: number): string[] {
  const exist = ins.exist_clause?.trim() || '(clause text unavailable)';
  const explanation = ins.explanation?.trim() || '(none)';
  const score =
    ins.similarity_score != null && !Number.isNaN(Number(ins.similarity_score))
      ? `Similarity score: ${ins.similarity_score}\n`
      : '';
  return [
    `--- Existing clause insight ${rank} ---`,
    score ? score.trimEnd() : null,
    'Existing clause text:',
    exist,
    '',
    'Explanation (why this clause may satisfy the client request):',
    explanation,
    '',
  ].filter((l) => l !== null) as string[];
}

function formatPastNegotiationBlock(
  opt: PastAgreementOption,
  rank: number,
  playbookPlainTextById: Record<string, string>,
  playbookFileNameById: Record<string, string>,
  recordNumberById?: Map<string, number>,
): string[] {
  const text =
    (opt.id && playbookPlainTextById[opt.id]?.trim()) ||
    opt.text?.trim() ||
    '(text unavailable)';
  const source =
    (opt.id && playbookFileNameById[opt.id]?.trim()) || opt.id || '(unknown source)';
  const explanation = opt.explanation?.trim() || '(none)';
  const similarity =
    opt.similarity != null && !Number.isNaN(Number(opt.similarity))
      ? `Similarity score: ${opt.similarity}\n`
      : '';
  const archiveRecordNum = opt.id ? recordNumberById?.get(opt.id) : undefined;

  return [
    `--- Match ${rank} ---`,
    archiveRecordNum != null
      ? `Playbook archive record: Record ${archiveRecordNum} (cite this number, not Match ${rank})`
      : null,
    `Source: ${source}`,
    similarity ? similarity.trimEnd() : null,
    'Past negotiation text:',
    text,
    '',
    'Explanation (how this match relates to the client request):',
    explanation,
    '',
  ].filter((l) => l !== null) as string[];
}

/** Prepends mode instructions so the model respects BoC vs archive vs both. */
export function applyAskAgentContextSourceBanner(
  body: string,
  source: AskAgentContextSource | null | undefined,
): string {
  const trimmed = body.trim();
  if (!trimmed || !source) return trimmed;
  if (source === 'bank_of_changes') {
    return (
      '=== ASK AGENT MODE: BANK OF CHANGES ONLY ===\n' +
      'Use ONLY the "Bank of Changes suggestions" section in this context. ' +
      'Reference options by their "Bank of Changes option N" labels. ' +
      'Do NOT cite project playbook archive records (Record 1, Record 2, …) or matched past negotiations.\n\n' +
      trimmed
    );
  }
  if (source === 'past_negotiations') {
    return (
      '=== ASK AGENT MODE: PROJECT NEGOTIATION ARCHIVE ===\n' +
      'Use matched past negotiations for this clause and the PROJECT PLAYBOOK archive below. ' +
      'Do NOT use Bank of Changes suggestions for this session.\n\n' +
      trimmed
    );
  }
  return (
    '=== ASK AGENT MODE: BANK OF CHANGES + NEGOTIATION ARCHIVE ===\n' +
    'Use Bank of Changes options, matched past negotiations, and the project playbook archive as needed.\n\n' +
    trimmed
  );
}

/** Clause focus first, then full project playbook; respects max context size. */
export function combineAskAgentContext(
  clausePrefix: string,
  playbookBody: string,
  options?: { includePlaybookArchive?: boolean; contextSource?: AskAgentContextSource | null },
): string {
  const includePlaybookArchive = options?.includePlaybookArchive !== false;
  const separator = '\n\n=== PROJECT PLAYBOOK (past negotiations archive) ===\n\n';
  const playbookPart = includePlaybookArchive ? (playbookBody || '').trim() : '';
  let out = applyAskAgentContextSourceBanner(clausePrefix.trim(), options?.contextSource);
  if (playbookPart) {
    out += separator + playbookPart;
  }
  if (out.length <= PLAYBOOK_CONTEXT_MAX_CHARS) {
    return out;
  }

  const note = '\n\n[Truncated: context exceeded size limit]';
  const prefixLen = clausePrefix.trim().length;
  const sepLen = playbookPart ? separator.length : 0;
  const budgetForPlaybook = PLAYBOOK_CONTEXT_MAX_CHARS - prefixLen - sepLen - note.length;

  if (budgetForPlaybook > 200 && playbookPart) {
    return (
      clausePrefix.trim() +
      separator +
      playbookPart.slice(0, budgetForPlaybook) +
      note
    );
  }

  return clausePrefix.trim().slice(0, PLAYBOOK_CONTEXT_MAX_CHARS - note.length) + note;
}
